/**
 * EntryPicker.jsx
 * 
 * Modal for selecting entries to add to a page.
 * Shows all available entries with search/filter.
 * 
 * Features:
 * - Grid view of all entries
 * - Search by date or text
 * - Filter by type (photos, milestones, etc.)
 * - Visual indicator for already-used entries
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useEditor } from './EditorContext';
import useBookDraft from './useBookDraft';
import {
  X,
  Search,
  Image as ImageIcon,
  Star,
  FileText,
  Calendar,
  Check,
  Loader2
} from 'lucide-react';

export default function EntryPicker({ page, babyBookId, onClose }) {
  const { addEntryToPage, state } = useEditor();
  const { fetchAllEntries } = useBookDraft(null, babyBookId);
  
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'photos' | 'milestones' | 'text'
  
  // Get all entry IDs currently used in any page
  const usedEntryIds = useMemo(() => {
    const ids = new Set();
    state.pages.forEach(p => {
      p.entries?.forEach(id => ids.add(id));
    });
    return ids;
  }, [state.pages]);
  
  // Load all entries on mount
  useEffect(() => {
    const loadEntries = async () => {
      setLoading(true);
      const allEntries = await fetchAllEntries();
      setEntries(allEntries);
      setLoading(false);
    };
    loadEntries();
  }, [fetchAllEntries]);
  
  // Filter entries based on search and filter
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesText = entry.text?.toLowerCase().includes(query);
        const matchesDate = entry.timestamp?.toLocaleDateString().includes(query);
        if (!matchesText && !matchesDate) return false;
      }
      
      // Type filter
      switch (filter) {
        case 'photos':
          return entry.photoCount > 0;
        case 'milestones':
          return entry.isMilestone;
        case 'text':
          return entry.text && entry.text.length > 0;
        default:
          return true;
      }
    });
  }, [entries, searchQuery, filter]);
  
  // Handle adding entry to page
  const handleAddEntry = useCallback((entryId) => {
    if (!page) return;
    addEntryToPage(page.id, entryId);
  }, [page, addEntryToPage]);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Add Content to Page {page?.sortOrder + 1}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Search and filters */}
        <div className="p-4 border-b space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search entries..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent"
            />
          </div>
          
          {/* Filter tabs */}
          <div className="flex gap-2">
            <FilterButton 
              active={filter === 'all'} 
              onClick={() => setFilter('all')}
              icon={null}
              label="All"
            />
            <FilterButton 
              active={filter === 'photos'} 
              onClick={() => setFilter('photos')}
              icon={ImageIcon}
              label="Photos"
            />
            <FilterButton 
              active={filter === 'milestones'} 
              onClick={() => setFilter('milestones')}
              icon={Star}
              label="Milestones"
            />
            <FilterButton 
              active={filter === 'text'} 
              onClick={() => setFilter('text')}
              icon={FileText}
              label="Text"
            />
          </div>
        </div>
        
        {/* Entry grid */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-ocean-600" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No entries found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filteredEntries.map(entry => {
                const isUsed = usedEntryIds.has(entry.id);
                const isOnCurrentPage = page?.entries?.includes(entry.id);
                
                return (
                  <button
                    key={entry.id}
                    onClick={() => handleAddEntry(entry.id)}
                    disabled={isOnCurrentPage}
                    className={`
                      relative rounded-lg overflow-hidden aspect-square
                      transition-all group
                      ${isOnCurrentPage 
                        ? 'ring-2 ring-ocean-500 opacity-50 cursor-not-allowed' 
                        : isUsed
                          ? 'ring-1 ring-gray-300 hover:ring-ocean-300'
                          : 'hover:ring-2 hover:ring-ocean-500'
                      }
                    `}
                  >
                    {/* Entry thumbnail or placeholder */}
                    {entry.thumbnailUrl ? (
                      <img 
                        src={entry.thumbnailUrl} 
                        alt="" 
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <FileText className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                    
                    {/* Overlay with info */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent">
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-white text-xs truncate">
                          {entry.timestamp?.toLocaleDateString()}
                        </p>
                        {entry.text && (
                          <p className="text-white/80 text-xs truncate">
                            {entry.text}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Milestone badge */}
                    {entry.isMilestone && (
                      <div className="absolute top-2 left-2">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      </div>
                    )}
                    
                    {/* Photo count badge */}
                    {entry.photoCount > 1 && (
                      <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
                        {entry.photoCount}
                      </div>
                    )}
                    
                    {/* Already on this page indicator */}
                    {isOnCurrentPage && (
                      <div className="absolute inset-0 bg-ocean-500/30 flex items-center justify-center">
                        <div className="bg-ocean-500 text-white p-2 rounded-full">
                          <Check className="w-5 h-5" />
                        </div>
                      </div>
                    )}
                    
                    {/* Used elsewhere indicator */}
                    {isUsed && !isOnCurrentPage && (
                      <div className="absolute top-2 right-2 bg-gray-500/80 text-white text-xs px-1.5 py-0.5 rounded">
                        Used
                      </div>
                    )}
                    
                    {/* Hover add indicator */}
                    {!isOnCurrentPage && (
                      <div className="absolute inset-0 bg-ocean-500/0 group-hover:bg-ocean-500/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="bg-white text-ocean-600 px-3 py-1 rounded-full text-sm font-medium shadow">
                          + Add
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {filteredEntries.length} entries available
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function FilterButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
        transition-colors
        ${active 
          ? 'bg-ocean-100 text-ocean-800' 
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }
      `}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
}
